import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { LanguageSwitcher } from './LanguageSwitcher'

type LegalPageKind = 'privacy' | 'terms'

interface LegalSection {
  title: string
  paragraphs?: string[]
  bullets?: string[]
}

export function LegalPage({ kind }: { kind: LegalPageKind }) {
  const { t, i18n } = useTranslation()
  const sections = t(`legalPages.${kind}.sections`, {
    returnObjects: true,
  }) as unknown as LegalSection[]

  useEffect(() => {
    const previousTitle = document.title
    const description = document.querySelector<HTMLMetaElement>(
      'meta[name="description"]',
    )
    const previousDescription = description?.content
    const canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]')
    const previousCanonical = canonical?.href
    const openGraphUrl = document.querySelector<HTMLMetaElement>('meta[property="og:url"]')
    const previousOpenGraphUrl = openGraphUrl?.content
    const legalUrl = `https://arubaanimalradar.com/${kind}`
    document.title = t(`legalPages.${kind}.metaTitle`)
    if (description) {
      description.content = t(`legalPages.${kind}.metaDescription`)
    }
    if (canonical) canonical.href = legalUrl
    if (openGraphUrl) openGraphUrl.content = legalUrl

    return () => {
      document.title = previousTitle
      if (description && previousDescription !== undefined) {
        description.content = previousDescription
      }
      if (canonical && previousCanonical) canonical.href = previousCanonical
      if (openGraphUrl && previousOpenGraphUrl !== undefined) {
        openGraphUrl.content = previousOpenGraphUrl
      }
    }
  }, [i18n.resolvedLanguage, kind, t])

  return (
    <div className="info-shell legal-shell">
      <header className="info-header">
        <Link className="info-brand" to="/">
          <img src="/brand/logo-header.webp" alt="" width="48" height="48" />
          <span className="info-brand-text">
            Aruba <em>Animal</em> Radar
          </span>
        </Link>
        <div className="info-header-actions legal-header-actions">
          <Link className="btn" to={kind === 'privacy' ? '/terms' : '/privacy'}>
            {t(kind === 'privacy' ? 'legal.terms' : 'legal.privacy')}
          </Link>
          <LanguageSwitcher />
          <Link className="btn btn-primary" to="/">
            {t('info.openMap')}
          </Link>
        </div>
      </header>

      <main className="info-page legal-page">
        <section className="info-hero legal-hero">
          <div className="info-kicker">{t('legal.kicker')}</div>
          <h1>{t(`legalPages.${kind}.title`)}</h1>
          <p>{t(`legalPages.${kind}.intro`)}</p>
          <time dateTime="2026-07-19">{t('legal.effectiveDate')}</time>
        </section>

        {sections.map((section, index) => (
          <section
            className="info-section legal-section"
            key={`${kind}-${section.title}`}
          >
            <div className="info-section-number">
              {String(index + 1).padStart(2, '0')}
            </div>
            <div>
              <h2>{section.title}</h2>
              {section.paragraphs?.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
              {section.bullets && (
                <ul>
                  {section.bullets.map((bullet) => (
                    <li key={bullet}>{bullet}</li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        ))}
      </main>

      <footer className="info-footer legal-footer">
        <span>Aruba Animal Radar</span>
        <nav aria-label={t('legal.navigation')}>
          <Link to="/privacy">{t('legal.privacy')}</Link>
          <Link to="/terms">{t('legal.terms')}</Link>
          <Link to="/information">{t('nav.information')}</Link>
          <Link to="/">{t('info.openMap')}</Link>
        </nav>
      </footer>
    </div>
  )
}
